import type { AssetCategory, AssetLane, Capacity, CoachCandidate, ClientAssetProjection, DailyAsset, DailyPlan } from './types'

export const laneMeta: Record<AssetLane, { label: string; description: string }> = {
  self: {
    label: 'Self development',
    description: 'Lead your own attention, values and behaviour before trying to influence the relationship.'
  },
  relationship: {
    label: 'Relationship development',
    description: 'Understand your wife’s actual world, contribute reliably and build a relationship that keeps renewing.'
  },
  fatherhood: {
    label: 'Fatherhood & family',
    description: 'Create the family atmosphere your children will one day carry into their own relationships.'
  }
}

const categoryDescription: Record<AssetCategory, string> = {
  self_leadership: 'Turn values into visible choices, clean boundaries and promises you can be trusted to keep.',
  reflection_values: 'See the pattern honestly, without turning one difficult moment into a diagnosis or identity.',
  regulation_presence: 'Create enough space between state and action to respond with strength and care.',
  understand_listen: 'Learn her current experience through curiosity and accurate listening rather than assumption.',
  love_in_action: 'Express care in ways that are personal, practical and free from expectation of repayment.',
  shared_load: 'Notice, own and complete the practical work of family life without needing to be managed.',
  repair_communication: 'Contain conflict, own impact and turn apology into a changed process.',
  fatherhood_presence: 'Offer children attention, steadiness, play and an honest model of adult repair.'
}

function asset(asset: Omit<DailyAsset, 'categoryDescription'>): DailyAsset {
  return { ...asset, categoryDescription: categoryDescription[asset.category] }
}

export const starterAssets: DailyAsset[] = [
  asset({
    assetId: 'self-arrival-001',
    title: 'The 90-second arrival',
    lane: 'self',
    category: 'regulation_presence',
    categoryLabel: 'Regulation & presence',
    purpose: 'Notice your state before it chooses your tone.',
    instructions: ['Place both feet down and take three slower exhales.', 'Name: body, emotion, story, urge and value.', 'Choose the next behaviour that matches the value.'],
    responsePrompt: 'What value do you want your next action to embody?',
    responseKind: 'short_text',
    durationMinutes: 2,
    effort: 'very_low',
    whyToday: 'You asked to become more deliberate during busy transitions.',
    completionSignal: 'You named one value-aligned next action.'
  }),
  asset({
    assetId: 'self-values-001',
    title: 'Make presence visible',
    lane: 'self',
    category: 'self_leadership',
    categoryLabel: 'Self-leadership',
    purpose: 'Turn “family first” into something your family can actually experience.',
    instructions: ['Choose one value you claim matters.', 'Name one visible behaviour that would prove it today.', 'Give it a time and place.'],
    responsePrompt: 'Write the behaviour, time and place.',
    responseKind: 'plan',
    durationMinutes: 4,
    effort: 'low',
    whyToday: 'Your profile prioritises presence and reliable follow-through.',
    completionSignal: 'A specific behaviour is planned.'
  }),
  asset({
    assetId: 'self-pattern-001',
    title: 'Camera before story',
    lane: 'self',
    category: 'reflection_values',
    categoryLabel: 'Reflection & values',
    purpose: 'Separate what happened from the meaning your protection system added.',
    instructions: ['Write only what a camera would have recorded.', 'Write the story you added.', 'List two other plausible meanings.'],
    responsePrompt: 'What did the camera see, and what story did you add?',
    responseKind: 'long_text',
    durationMinutes: 7,
    effort: 'medium',
    whyToday: 'A clearer observation gives you more choices in the next conversation.',
    completionSignal: 'Observation and interpretation are written separately.'
  }),
  asset({
    assetId: 'self-promise-001',
    title: 'Close one open promise',
    lane: 'self',
    category: 'self_leadership',
    categoryLabel: 'Self-leadership',
    purpose: 'Build trust through closure rather than another ambitious declaration.',
    instructions: ['Name one unfinished promise.', 'Complete it, renegotiate it early or release it by agreement.', 'Record the next honest step.'],
    responsePrompt: 'Which promise will you close, and how?',
    responseKind: 'short_text',
    durationMinutes: 5,
    effort: 'low',
    whyToday: 'Reliability is one of the foundations you chose to strengthen.',
    completionSignal: 'One promise has a clear outcome or renegotiation.'
  }),
  asset({
    assetId: 'relationship-listen-001',
    title: 'Ask what support means today',
    lane: 'relationship',
    category: 'understand_listen',
    categoryLabel: 'Understand & listen',
    purpose: 'Let her current words outrank assumptions about what should help.',
    instructions: ['Choose a calm moment.', 'Ask whether listening, practical help, quiet, affection, planning or something else would help.', 'Receive the answer without correcting it.'],
    responsePrompt: 'What did she actually ask for? Label this as your report of her words.',
    responseKind: 'long_text',
    durationMinutes: 8,
    effort: 'low',
    whyToday: 'The coming week carries extra logistical load.',
    consentCue: 'Ask at a suitable moment; she can decline the conversation.',
    completionSignal: 'You asked and reflected her answer without solving over it.'
  }),
  asset({
    assetId: 'relationship-love-001',
    title: 'Admiration with evidence',
    lane: 'relationship',
    category: 'love_in_action',
    categoryLabel: 'Love in action',
    purpose: 'Make appreciation specific enough to be felt.',
    instructions: ['Notice one quality through a recent real example.', 'Name its impact on you or the family.', 'Share it without attaching a request.'],
    responsePrompt: 'What did you notice and appreciate?',
    responseKind: 'short_text',
    durationMinutes: 3,
    effort: 'very_low',
    whyToday: 'Specific recognition supports your goal of helping her feel seen.',
    completionSignal: 'A specific appreciation was shared without an agenda.'
  }),
  asset({
    assetId: 'relationship-load-001',
    title: 'Notice and finish',
    lane: 'relationship',
    category: 'shared_load',
    categoryLabel: 'Shared load',
    purpose: 'Carry a complete practical loop without asking her to manage the finish.',
    instructions: ['Choose an already-agreed household domain.', 'Find the next unfinished loop.', 'Notice, plan, complete and verify it.'],
    responsePrompt: 'What full loop did you own today?',
    responseKind: 'short_text',
    durationMinutes: 15,
    effort: 'medium',
    whyToday: 'Practical relief is part of love, not separate from it.',
    completionSignal: 'The outcome is complete without a reminder or expectation of praise.'
  }),
  asset({
    assetId: 'relationship-repair-001',
    title: 'The small return',
    lane: 'relationship',
    category: 'repair_communication',
    categoryLabel: 'Repair & communication',
    purpose: 'Repair a small unfinished moment before it hardens into distance.',
    instructions: ['Name your specific behaviour without “if” or “but”.', 'Acknowledge likely impact and ask for correction.', 'Ask whether now is a suitable time to repair.'],
    responsePrompt: 'Draft your first two repair sentences.',
    responseKind: 'long_text',
    durationMinutes: 10,
    effort: 'medium',
    whyToday: 'One unfinished moment was carried into this week.',
    consentCue: 'If either person is afraid or unsafe, do not use a joint repair exercise.',
    completionSignal: 'You owned the behaviour and made a clean invitation to repair.',
    isDeep: true
  }),
  asset({
    assetId: 'relationship-arrival-001',
    title: 'Protect the first ten minutes',
    lane: 'relationship',
    category: 'love_in_action',
    categoryLabel: 'Love in action',
    purpose: 'Create a calmer transition from work mode into family presence.',
    instructions: ['Put the phone away before entering.', 'Take one breath at the door.', 'Offer ten minutes of undivided arrival before logistics where possible.'],
    responsePrompt: 'What will help you protect this transition?',
    responseKind: 'plan',
    durationMinutes: 10,
    effort: 'low',
    whyToday: 'Work-to-home transitions are a high-leverage place for your presence goal.',
    completionSignal: 'The first ten minutes were intentionally protected.'
  }),
  asset({
    assetId: 'relationship-question-002',
    title: 'What would make home 10% easier?',
    lane: 'relationship',
    category: 'understand_listen',
    categoryLabel: 'Understand & listen',
    purpose: 'Learn one practical improvement from her actual experience.',
    instructions: ['Ask one clean question.', 'Do not defend the current system.', 'Choose one next step only after understanding.'],
    responsePrompt: 'What did she say would make home easier?',
    responseKind: 'long_text',
    durationMinutes: 8,
    effort: 'low',
    whyToday: 'Your weekly load review has one unanswered question.',
    consentCue: 'One question is enough; do not turn this into an interview.',
    completionSignal: 'Her answer was heard and one possible next step was named.'
  }),
  asset({
    assetId: 'father-child-001',
    title: 'Child-led five',
    lane: 'fatherhood',
    category: 'fatherhood_presence',
    categoryLabel: 'Fatherhood presence',
    purpose: 'Let your child experience attention without performance or correction.',
    instructions: ['Put the device away.', 'Follow the child’s interest for five minutes.', 'Ask one curious question and resist taking over.'],
    responsePrompt: 'What mattered to your child in those five minutes?',
    responseKind: 'short_text',
    durationMinutes: 5,
    effort: 'very_low',
    whyToday: 'Small repeated moments shape the family atmosphere you want to create.',
    completionSignal: 'Five child-led, device-free minutes happened.'
  }),
  asset({
    assetId: 'father-model-001',
    title: 'Model the repair',
    lane: 'fatherhood',
    category: 'fatherhood_presence',
    categoryLabel: 'Fatherhood presence',
    purpose: 'Show that strength includes owning a mistake and changing the process.',
    instructions: ['Choose an age-appropriate moment.', 'Name what you did without adult details.', 'Apologise and name what you will do differently.'],
    responsePrompt: 'What simple repair could your child safely witness?',
    responseKind: 'long_text',
    durationMinutes: 6,
    effort: 'medium',
    whyToday: 'Children learn repair from atmosphere and behaviour, not lectures.',
    completionSignal: 'A simple, non-burdening repair was modelled.'
  })
]

export function createDailyPlan(capacity: Capacity): DailyPlan {
  const focusByCapacity: Record<Capacity, string[]> = {
    low: ['self-arrival-001', 'relationship-love-001'],
    normal: ['self-arrival-001', 'relationship-load-001', 'father-child-001'],
    spacious: ['self-values-001', 'relationship-repair-001', 'father-child-001']
  }
  const focusAssetIds = focusByCapacity[capacity]
  return {
    planId: `demo-${capacity}-2026-08-15`,
    localDate: '2026-08-15',
    focusAssetIds,
    shelfAssetIds: starterAssets.map((item) => item.assetId).filter((id) => !focusAssetIds.includes(id)),
    revision: 1
  }
}

export function getNextEligibleAsset(currentId: string, assets: DailyAsset[]): DailyAsset {
  const current = assets.find((item) => item.assetId === currentId)
  if (!current) throw new Error(`Unknown asset: ${currentId}`)
  const categoryPeers = assets.filter((item) => item.category === current.category && item.assetId !== currentId)
  if (categoryPeers.length > 0) return categoryPeers[0]
  const lanePeers = assets.filter((item) => item.lane === current.lane && item.assetId !== currentId)
  if (lanePeers.length === 0) return current
  return lanePeers[0]
}

const guidedCandidatePrototype = {
  plainLanguageExplanation: 'This is a small action you can try without having to explain yourself first.',
  recognitionStory: 'A man notices he brings work speed through the front door. He pauses once, without judging himself, and chooses a warmer first moment.',
  realLifeExamples: ['Pausing before entering home.', 'Sending one small message of appreciation.'],
  responseOptions: ['I tried this today', 'I can see how this might help', 'I am not sure yet', 'This does not apply to me'],
  exampleAnswers: ['I do not know what changed yet.', 'I noticed I was still carrying work urgency.', 'The pause helped me choose how I wanted to arrive.'],
  responseOptional: true,
  freeTextOptional: true,
  lighterAlternative: 'Take one slow breath and notice what pace you are carrying.',
}

export const coachCandidates: CoachCandidate[] = [
  {
    candidateId: 'candidate-relationship-transition-001',
    lane: 'relationship',
    category: 'love_in_action',
    coachReason: 'Generated because the weekly record shows three rushed work-to-home transitions, while the user’s highest-ranked value is presence. A low-demand arrival ritual adds behavioural practice without asking the partner to carry another conversation.',
    coachReasonCodes: ['presence_goal', 'transition_friction', 'low_demand_win'],
    sourceFactIds: ['fact-value-presence', 'fact-transition-rushed', 'fact-capacity-weekday-low'],
    generatedAsset: {
      ...guidedCandidatePrototype,
      title: 'Come home before you enter',
      purpose: 'Move from work urgency into family presence before opening the door.',
      instructions: ['Pause before entering.', 'Take three slower breaths.', 'Choose the quality you want your family to meet.'],
      responsePrompt: 'What quality did you choose, and what changed?',
      responseKind: 'long_text',
      completionSignal: 'A deliberate transition happened before entering.'
    },
    editableDraft: {
      ...guidedCandidatePrototype,
      title: 'Come home before you enter',
      purpose: 'Move from work urgency into family presence before opening the door.',
      instructions: ['Pause before entering.', 'Take three slower breaths.', 'Choose the quality you want your family to meet.'],
      responsePrompt: 'What quality did you choose, and what changed?',
      responseKind: 'long_text',
      completionSignal: 'A deliberate transition happened before entering.'
    },
    reviewState: 'awaiting_review'
  },
  {
    candidateId: 'candidate-self-integrity-001',
    lane: 'self',
    category: 'self_leadership',
    coachReason: 'Generated because one promise has remained open across two reviews. The candidate targets reliable closure rather than deeper interpretation and uses a concrete outcome that can be reviewed next week.',
    coachReasonCodes: ['open_promise', 'reliability_goal', 'behaviour_before_analysis'],
    sourceFactIds: ['fact-open-promise-004', 'fact-value-reliability'],
    generatedAsset: {
      ...guidedCandidatePrototype,
      title: 'Close the loop cleanly',
      purpose: 'Turn an open promise into completion, early renegotiation or an agreed release.',
      instructions: ['Name the promise.', 'Choose complete, renegotiate or release.', 'Communicate before the old deadline silently passes.'],
      responsePrompt: 'Which clean outcome did you choose?',
      responseKind: 'short_text',
      completionSignal: 'The promise has a clear and communicated outcome.'
    },
    editableDraft: {
      ...guidedCandidatePrototype,
      title: 'Close the loop cleanly',
      purpose: 'Turn an open promise into completion, early renegotiation or an agreed release.',
      instructions: ['Name the promise.', 'Choose complete, renegotiate or release.', 'Communicate before the old deadline silently passes.'],
      responsePrompt: 'Which clean outcome did you choose?',
      responseKind: 'short_text',
      completionSignal: 'The promise has a clear and communicated outcome.'
    },
    reviewState: 'awaiting_review'
  }
]

export function projectCandidateForClient(candidate: CoachCandidate): ClientAssetProjection {
  return {
    assetId: `published-${candidate.candidateId}`,
    lane: candidate.lane,
    category: candidate.category,
    ...candidate.editableDraft
  }
}
